// Plik: src/components/ServiceWebClient.tsx
"use client";

import Link from "next/link";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// --- KOMPONENTY POMOCNICZE ---

const BorderBeam = ({ duration = 10, delay = 0 }) => {
  return (
    <div className="absolute inset-0 rounded-xl pointer-events-none overflow-hidden">
        <motion.div
            className="absolute -inset-[100%]"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: duration, repeat: Infinity, ease: "linear", delay: delay }}
            style={{
                background: "conic-gradient(from 0deg at 50% 50%, rgba(255,255,255,1) 0deg, rgba(255,255,255,0.3) 180deg, rgba(255,255,255,1) 360deg)",
                filter: "blur(8px)",
            }}
        />
        <div className="absolute inset-[1px] bg-inherit rounded-xl z-10" />
    </div>
  );
};

const GridPattern = () => (
  <div className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" 
       style={{ 
         backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', 
         backgroundSize: '40px 40px' 
       }}>
  </div>
);

const GridPatternDark = () => (
  <div className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" 
       style={{ 
         backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', 
         backgroundSize: '40px 40px' 
       }}>
  </div>
);

const CheckIcon = () => (
  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
  </div>
);

const XIcon = () => (
  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-500">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
  </div>
);

const TerminalIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 17 10 11 4 5"></polyline><line x1="12" y1="19" x2="20" y2="19"></line></svg>
);

const PlusIcon = ({ isOpen }: { isOpen: boolean }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}>
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);

const CursorIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.3))" }}>
        <path d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z" fill="black" stroke="white" strokeWidth="2" strokeLinejoin="round"/>
    </svg>
);

// --- KOMPONENT FAQ ITEM ---
const FaqItem = ({ question, answer, isOpen, onClick }: { question: string, answer: string, isOpen: boolean, onClick: () => void }) => {
    return (
        <div className="border-b border-gray-100 last:border-0">
            <button 
                onClick={onClick}
                className="w-full py-6 flex items-center justify-between text-left focus:outline-none group"
            >
                <span className={`text-lg font-medium transition-colors duration-300 ${isOpen ? "text-blue-600" : "text-gray-900 group-hover:text-blue-600"}`}>
                    {question}
                </span>
                <span className={`ml-6 flex-shrink-0 text-gray-400 group-hover:text-blue-600 transition-colors`}>
                    <PlusIcon isOpen={isOpen} />
                </span>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        <div className="pb-6 text-gray-500 leading-relaxed text-base">
                            {answer}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default function ServiceWebClient() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqData = [
    { 
        q: "Jaki jest koszt inwestycji w dedykowany serwis?", 
        a: "Każdy projekt wyceniamy indywidualnie, opierając się na zakresie funkcjonalności i celach biznesowych. Nasze realizacje typu Landing Page zaczynają się od X zł, natomiast rozbudowane serwisy korporacyjne to inwestycja rzędu Y zł. Skontaktuj się z nami, aby otrzymać precyzyjną estymację." 
    },
    { 
        q: "Czy zapewniacie panel CMS do zarządzania treścią?", 
        a: "Tak. Nie uzależniamy Cię od deweloperów. Wdrażamy nowoczesne systemy Headless CMS (np. Sanity.io), które oferują intuicyjny interfejs 'przeciągnij i upuść'. Będziesz mógł samodzielnie edytować teksty, zdjęcia i publikować artykuły w czasie rzeczywistym." 
    },
    { 
        q: "Jaki jest czas realizacji (Time-to-Market)?", 
        a: "Szanujemy Twój czas. Standardowy proces trwa od 2 do 4 tygodni. Pierwszy tydzień to warsztaty i design, kolejne to development i testy. Dzięki naszej bibliotece komponentów pracujemy znacznie szybciej niż typowe software house'y." 
    },
    { 
        q: "Czy strona jest zoptymalizowana pod SEO?", 
        a: "Absolutnie. SEO to u nas standard, nie opcja dodatkowa. Twoja strona będzie miała perfekcyjną strukturę HTML5, metadane, mapę witryny i błyskawiczne czasy ładowania (Core Web Vitals), co Google nagradza wyższymi pozycjami." 
    },
    { 
        q: "Czy oferujecie wsparcie po wdrożeniu?", 
        a: "Tak. Po uruchomieniu strony nie zostawiamy Cię samego. Oferujemy pakiety utrzymaniowe (SLA), w ramach których dbamy o aktualizacje bezpieczeństwa, monitoring 24/7 i drobne prace rozwojowe." 
    }
  ];

  return (
    <main className="bg-[#F7F7F8] w-full min-h-screen relative selection:bg-blue-200 font-sans text-gray-900">
      

      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-12 lg:pt-48 lg:pb-12 px-6 overflow-hidden">
        <GridPattern />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[50vh] bg-blue-500/5 blur-[120px] pointer-events-none rounded-full"></div>

        <div className="mx-auto max-w-[1250px] relative z-10">
            <div className="max-w-4xl">
                <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-3 py-1 text-xs font-bold text-blue-600 mb-6 shadow-sm"
                >
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                    </span>
                    STRONY INTERNETOWE PREMIUM
                </motion.div>
                
                <motion.h1 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-5xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-8"
                >
                    Strony, które <br/>
                    <span className="text-blue-600">zarabiają pieniądze.</span>
                </motion.h1>
                
                <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-xl text-gray-600 leading-relaxed max-w-2xl font-medium"
                >
                    Twoja strona to wizytówka Twojej firmy. Tworzymy serwisy, które działają tak płynnie i szybko jak platformy <strong>Netflix, Twitch czy Nike</strong>. Skupiamy się na tym, by każdy odwiedzający stał się Twoim klientem.
                </motion.p>
                
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-10 flex flex-wrap gap-4"
                >
                    <Link href="/kontakt" className="rounded-full bg-black px-8 py-4 text-sm font-bold text-white transition hover:bg-gray-800 shadow-xl hover:shadow-2xl hover:-translate-y-1">
                        Rozpocznij projekt
                    </Link>
                    <Link href="#technologia" className="rounded-full bg-white border border-gray-200 px-8 py-4 text-sm font-bold text-gray-900 transition hover:bg-gray-50 hover:border-gray-300">
                        Zobacz standardy
                    </Link>
                </motion.div>
            </div>
        </div>
      </section>

      {/* --- PORÓWNANIE (PROBLEM VS ROZWIĄZANIE) --- */}
      <section className="pt-10 pb-20 lg:pt-0 lg:pb-32 px-6 relative">
        <GridPattern />
        <div className="mx-auto max-w-[1250px] relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                
                {/* KARTA 1: PROBLEM */}
                <div className="relative rounded-3xl bg-white border border-gray-200 p-8 lg:p-12 shadow-sm transition hover:shadow-md">
                    <div className="mb-8">
                        <div className="inline-block p-3 rounded-xl bg-red-50 text-red-500 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-exclamation-triangle" viewBox="0 0 16 16">
                                <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.15.15 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.2.2 0 0 1-.054.06.1.1 0 0 1-.066.017H1.146a.1.1 0 0 1-.066-.017.2.2 0 0 1-.054-.06.18.18 0 0 1 .002-.183L7.884 2.073a.15.15 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767z"/>
                                <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Standardowa Strona</h3>
                        <p className="text-gray-500">Większość stron w sieci wygląda podobnie i działa wolno. To sprawia, że klienci uciekają do konkurencji.</p>
                    </div>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Długi czas ładowania</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Szablonowy, "tani" wygląd</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Trudna obsługa na telefonie</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-600">
                            <XIcon /> <span>Brak zabezpieczeń przed awariami</span>
                        </li>
                    </ul>
                </div>

                {/* KARTA 2: ROZWIĄZANIE */}
                <div className="relative rounded-3xl bg-white border border-blue-200 p-8 lg:p-12 shadow-2xl shadow-blue-900/5 ring-4 ring-blue-50/50">
                    <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-bl-xl rounded-tr-2xl uppercase tracking-wider">
                        Twój wybór
                    </div>
                    <div className="mb-8">
                        <div className="inline-block p-3 rounded-xl bg-blue-50 text-blue-600 mb-4">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Strona od Webpol</h3>
                        <p className="text-gray-500">Inżynieria nastawiona na wynik. Twoja strona będzie pracować na Twój sukces 24 godziny na dobę.</p>
                    </div>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Błyskawiczne działanie (jak w aplikacji)</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Unikalny design dopasowany do marki</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Wygodny panel do edycji treści (CMS)</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Lepsza widoczność w Google</span>
                        </li>
                        <li className="flex items-start gap-3 text-gray-800 font-medium">
                            <CheckIcon /> <span>Pełne bezpieczeństwo danych</span>
                        </li>
                    </ul>
                </div>

            </div>
        </div>
      </section>

      {/* --- TECHNOLOGIA (CIEMNA, FULL SCREEN DESKTOP) --- */}
      <section id="technologia" className="bg-[#0b0c0e] py-16 lg:py-0 lg:min-h-screen flex flex-col justify-center relative overflow-hidden z-30 border-t border-white/5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-blue-900/10 blur-[150px] pointer-events-none"></div>
        <div className="mx-auto max-w-[1250px] px-6 relative z-10 w-full">
          
          <div className="text-left lg:text-center max-w-3xl mx-0 lg:mx-auto mb-16">
             <h2 className="text-3xl lg:text-6xl font-bold tracking-tight text-white mb-6">
               Jakość, której ufają <br />
               <span className="text-gray-400">giganci.</span>
             </h2>
             <p className="text-sm lg:text-lg text-gray-500 max-w-2xl mx-0 lg:mx-auto">
               Nie używamy przestarzałych narzędzi. Twoja strona powstanie na fundamencie technologicznym, z którego korzystają największe marki świata.
             </p>
          </div>

          <div className="relative w-full max-w-4xl mx-auto mb-16 lg:mb-24">
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-gradient-to-r from-blue-600/20 to-purple-500/20 blur-[80px] rounded-full -z-10 opacity-60"></div>
             <div className="relative aspect-[16/9] lg:aspect-[21/9] w-full rounded-2xl overflow-hidden shadow-2xl bg-[#13151a] border border-white/5">
                 <BorderBeam duration={10} delay={0} />
                 <div className="absolute inset-[1px] bg-[#0b0c0e] rounded-2xl flex items-center justify-center overflow-hidden z-10">
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
                    <div className="text-center p-8">
                       <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/10">
                          <span className="text-2xl lg:text-3xl font-bold text-white">N</span>
                       </div>
                       <p className="text-gray-500 text-xs lg:text-sm uppercase tracking-widest font-bold">Next.js Enterprise Stack</p>
                    </div>
                 </div>
             </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 text-left max-w-5xl mx-auto">
             <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 border border-blue-500/20">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-white">Globalna Szybkość</h3>
                <p className="text-gray-500 leading-relaxed text-sm">Dzięki infrastrukturze Edge (Vercel), Twoja strona ładuje się błyskawicznie dla klienta z każdego zakątka świata.</p>
             </div>
             <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-500 border border-purple-500/20">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.243-8.25-3.285z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-white">Bezpieczeństwo</h3>
                <p className="text-gray-500 leading-relaxed text-sm">Statyczne generowanie treści sprawia, że Twoja strona jest twierdzą odporną na typowe ataki hakerskie.</p>
             </div>
             <div className="space-y-4">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 border border-green-500/20">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-white">Niezawodność</h3>
                <p className="text-gray-500 leading-relaxed text-sm">Brak problemów przy dużym ruchu. Twoja strona działa stabilnie nawet podczas kampanii reklamowych czy Black Friday.</p>
             </div>
          </div>
        </div>
      </section>

      {/* --- NIEWIDZIALNA PRZEWAGA TECHNICZNA --- */}
      <section className="bg-[#F7F7F8] py-16 lg:py-24 relative overflow-hidden border-t border-gray-200">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-12 lg:gap-16 px-6 lg:grid-cols-[1fr_400px] items-start">
            
            {/* LEWA KOLUMNA (Treść) */}
            <div className="flex flex-col gap-8">
                <div className="relative">
                    <div className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 mb-4">
                        <TerminalIcon /> TECHNICZNE BEZPIECZEŃSTWO
                    </div>
                    <h2 className="text-4xl font-bold tracking-tight text-gray-900 md:text-5xl leading-[1.15] mb-6">
                        Niewidzialna <br />
                        <span className="text-blue-600">przewaga techniczna.</span>
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed">
                        Podczas gdy Twoja konkurencja walczy z aktualizacjami wtyczek i wolnym ładowaniem, Ty masz narzędzie precyzyjnie dostrojone do sprzedaży.
                    </p>
                </div>

                {/* Grid zalet technicznych */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 mb-4 font-bold text-xl">🚀</div>
                        <h4 className="font-bold text-gray-900 mb-2">Core Web Vitals</h4>
                        <p className="text-sm text-gray-500">Zielone wyniki w testach Google. Lepsze SEO i niższy koszt reklam (Quality Score).</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 mb-4 font-bold text-xl">📱</div>
                        <h4 className="font-bold text-gray-900 mb-2">Full Responsive</h4>
                        <p className="text-sm text-gray-500">Perfekcyjne działanie na każdym ekranie - od iPhone'a po ekrany 4K.</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mb-4 font-bold text-xl">🛡️</div>
                        <h4 className="font-bold text-gray-900 mb-2">Zero Wtyczek</h4>
                        <p className="text-sm text-gray-500">Brak "bloatware". Tylko kod, który jest niezbędny. Zero dziur bezpieczeństwa.</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4 font-bold text-xl">☁️</div>
                        <h4 className="font-bold text-gray-900 mb-2">Edge Caching</h4>
                        <p className="text-sm text-gray-500">Treść serwowana z serwerów blisko użytkownika. Błyskawiczny czas reakcji.</p>
                    </div>
                </div>
            </div>

            {/* PRAWA KOLUMNA (Terminal) - UKRYTA NA MOBILE */}
            <div className="relative mt-8 lg:mt-0 hidden lg:block">
                {/* Karta Terminala */}
                <div className="rounded-xl bg-[#1e1e1e] p-4 shadow-2xl border border-gray-800 font-mono text-[11px] text-gray-400 leading-relaxed overflow-hidden">
                    <div className="flex items-center gap-2 mb-4 border-b border-gray-700 pb-3">
                        <div className="flex gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                        </div>
                        <span className="ml-2 text-gray-500">build-process — bash</span>
                    </div>
                    <div className="space-y-1">
                        <div><span className="text-green-400">➜</span> <span className="text-blue-400">~</span> npm run build</div>
                        <div className="text-gray-500">Creating an optimized production build...</div>
                        <div className="text-gray-500">Compiling...</div>
                        <div className="text-green-400">✓ Compiled successfully</div>
                        <div className="mt-2 text-gray-300">Route (app)                             Size     First Load JS</div>
                        <div className="text-gray-500">┌ ○ /                                    5.2 kB   84.1 kB</div>
                        <div className="text-gray-500">├ ○ /about                              2.1 kB   72.5 kB</div>
                        <div className="text-gray-500">└ λ /api/contact                         0 kB     0 kB</div>
                        <div className="mt-2 text-blue-400">info</div>
                        <div className="text-gray-300">Collecting page data...</div>
                        <div className="text-gray-300">Generating static pages (12/12)</div>
                        <div className="text-green-400">✓ Finalizing page optimization...</div>
                        <div className="mt-2"><span className="text-green-400">Done in 8.42s.</span></div>
                    </div>
                </div>

                {/* Karta "Wynik" */}
                <div className="absolute -bottom-10 -left-10 bg-white p-4 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-gray-100 flex items-center gap-4 animate-in slide-in-from-bottom-4 fade-in duration-1000">
                    <div className="relative w-16 h-16 flex items-center justify-center">
                        <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                            <path className="text-gray-100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                            <path className="text-green-500" strokeDasharray="100, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                        </svg>
                        <span className="absolute text-xl font-bold text-green-600">100</span>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400 uppercase font-bold tracking-wider">Performance</div>
                        <div className="text-sm font-bold text-gray-800">Perfect Score</div>
                        <div className="text-left"><span className="text-[10px] text-green-600 font-medium">Top 1% stron w sieci</span></div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* --- NOWOŚĆ: FILOZOFIA UX / PSYCHOLOGIA (DOPRACOWANA GRAFIKA) --- */}
      <section className="bg-white py-20 lg:py-32 px-6 border-t border-gray-200">
        <div className="mx-auto max-w-[1250px]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                
                {/* LEWA KOLUMNA: TREŚĆ */}
                <div>
                    <span className="text-blue-600 font-bold tracking-widest uppercase text-xs mb-4 block">Filozofia UX</span>
                    <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">Architektura Decyzji.</h2>
                    <div className="prose prose-lg text-gray-600">
                        <p>
                            Spędziłem lata analizując ścieżki użytkownika u gigantów takich jak <strong>Apple czy Google</strong>. Zrozumiałem jedną kluczową rzecz: 
                            to nie przypadek, że ich strony są tak proste. To precyzyjna inżynieria behawioralna.
                        </p>
                        <p className="mt-4">
                            Największym wrogiem sprzedaży jest <strong>Paradoks Wyboru</strong>. Gdy użytkownik otrzymuje zbyt wiele informacji naraz, następuje paraliż decyzyjny (Cognitive Load). W efekcie, zamiast kupić – wychodzi.
                        </p>
                        <p className="mt-4 font-medium text-gray-900">
                            Projektujemy strony tak, aby prowadziły klienta za rękę. Dajemy tylko tyle informacji, ile jest niezbędne do podjęcia decyzji "Tu i Teraz". To czysta psychologia w służbie technologii.
                        </p>
                    </div>
                </div>

                {/* PRAWA KOLUMNA: WIZUALIZACJA (CHAOS vs FOCUS) */}
                <div className="relative">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Karta 1: CHAOS */}
                        <div className="bg-red-50/50 border border-red-100 rounded-xl p-6 opacity-80 scale-95 blur-[0.5px] relative overflow-hidden transition-all hover:opacity-100 hover:blur-0">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                <span className="text-xs font-bold text-red-500 uppercase tracking-wide">Brak skupienia</span>
                            </div>
                            
                            {/* Chaotyczne elementy */}
                            <div className="space-y-3 relative">
                                <div className="h-4 w-3/4 bg-gray-300 rounded"></div>
                                <div className="h-2 w-full bg-gray-200 rounded"></div>
                                <div className="h-2 w-full bg-gray-200 rounded"></div>
                                
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                    <div className="h-12 bg-gray-200 rounded"></div>
                                    <div className="h-12 bg-gray-200 rounded"></div>
                                    <div className="h-12 bg-gray-200 rounded"></div>
                                </div>

                                {/* Rozpraszacze (Pop-ups) */}
                                <div className="absolute top-8 left-4 right-4 bg-white p-3 rounded shadow-lg border border-red-200 rotate-2 z-10">
                                    <div className="text-[10px] font-bold text-red-500 text-center mb-1">ZAPISZ SIĘ!</div>
                                    <div className="h-2 bg-gray-100 rounded w-full"></div>
                                </div>

                                <div className="absolute bottom-0 left-0 right-0 bg-gray-800 p-2 rounded-b-lg flex justify-between items-center opacity-90">
                                    <div className="h-1.5 w-16 bg-gray-500 rounded"></div>
                                    <div className="h-3 w-8 bg-blue-500 rounded"></div>
                                </div>
                            </div>
                            
                            <p className="text-center text-xs font-medium text-red-400 mt-6">Klient się gubi</p>
                        </div>

                        {/* Karta 2: FOCUS (WEBPOL) */}
                        <div className="bg-white border border-green-200 rounded-xl p-6 shadow-xl shadow-green-100 relative md:-ml-12 md:mt-12 ring-1 ring-green-50 overflow-hidden transform transition-transform hover:scale-105 duration-300">
                            <div className="absolute top-0 right-0 bg-green-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                                SKUTECZNOŚĆ <span className="font-normal opacity-70 ml-1">(Wizualizacja)</span>
                            </div>
                            
                            <div className="flex items-center gap-2 mb-6">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="text-xs font-bold text-green-600 uppercase tracking-wide">Pełne skupienie</span>
                            </div>
                            
                            <div className="space-y-5">
                                {/* Czysty nagłówek */}
                                <div className="h-5 w-2/3 bg-gray-900 rounded"></div>
                                
                                {/* Obraz produktu */}
                                <div className="h-24 w-full bg-blue-50 rounded-lg border border-blue-100 flex items-center justify-center relative overflow-hidden">
                                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-50 to-white opacity-50"></div>
                                    <span className="text-3xl relative z-10">✨</span>
                                </div>
                                
                                {/* Przycisk CTA */}
                                <div className="relative group w-full">
                                    <div className="h-12 w-full bg-black rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-lg tracking-wide group-hover:bg-gray-800 transition-colors">
                                        KUP TERAZ
                                    </div>
                                    
                                    {/* Kursor najeżdżający na przycisk */}
                                    <motion.div 
                                        className="absolute top-full left-1/2 z-20 pointer-events-none"
                                        initial={{ x: 30, y: 30, opacity: 0 }}
                                        whileInView={{ x: 10, y: -10, opacity: 1 }}
                                        transition={{ duration: 1.5, delay: 0.5, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                    >
                                        <CursorIcon />
                                    </motion.div>
                                </div>
                            </div>

                            <p className="text-center text-xs font-medium text-green-600 mt-6">Klient kupuje</p>
                        </div>

                    </div>
                </div>

            </div>
        </div>
      </section>

      {/* --- PROCES WSPÓŁPRACY (CIEMNA WERSJA, FULL SCREEN DESKTOP) --- */}
      <section id="proces" className="py-24 px-6 relative overflow-hidden bg-[#0b0c0e] border-t border-white/5 lg:py-0 lg:min-h-screen flex flex-col justify-center">
        <GridPatternDark />
        <div className="mx-auto max-w-[1000px] relative z-10 w-full">
            {/* NAGŁÓWEK DO LEWEJ NA MOBILE */}
            <div className="mb-16 text-left lg:text-center">
                <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">Przejrzysty proces.</h2>
                <p className="text-xl text-gray-400 max-w-2xl mx-0 lg:mx-auto">
                    Od pierwszej kawy do uruchomienia strony. Prowadzimy Cię za rękę przez każdy etap, bez technicznego żargonu.
                </p>
            </div>

            <div className="relative">
                {/* Linia łącząca */}
                <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-white/10 -z-10"></div>

                <div className="space-y-12 lg:space-y-0">
                    
                    {/* ETAP 1 (Lewo na Desktop) */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group lg:mb-12">
                        {/* TEKST */}
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-2 lg:order-1 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-blue-500/10 text-blue-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 01</span>
                            <h3 className="text-xl font-bold text-white mb-2">Discovery & Strategia</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Poznajemy Twój biznes. Analizujemy konkurencję, dobieramy słowa kluczowe i ustalamy architekturę informacji. To fundament sukcesu.
                            </p>
                        </div>
                        {/* KROPKA */}
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-blue-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-blue-500">1</span>
                        </div>
                        {/* DETALE */}
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-3 mt-4 lg:mt-0">
                             <ul className="text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Warsztat z klientem</li>
                                <li className="flex items-center gap-2">🔹 Analiza SEO</li>
                                <li className="flex items-center gap-2">🔹 Mapa strony (Sitemap)</li>
                             </ul>
                        </div>
                    </div>

                    {/* ETAP 2 (Prawo na Desktop) */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group lg:mb-12">
                        
                        {/* DETALE (Lista) */}
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-3 lg:order-1 mt-4 lg:mt-0">
                             {/* MOBILE: Block A (Left Aligned) */}
                             <ul className="lg:hidden text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Makieta Lo-Fi</li>
                                <li className="flex items-center gap-2">🔹 Moodboard</li>
                                <li className="flex items-center gap-2">🔹 UI Design w Figma</li>
                             </ul>

                             {/* DESKTOP: Block B (Right Aligned / Reversed) */}
                             <ul className="hidden lg:inline-block w-full text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5">
                                <li className="flex items-center gap-2 justify-end">Makieta Lo-Fi 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Moodboard 🔹</li>
                                <li className="flex items-center gap-2 justify-end">UI Design w Figma 🔹</li>
                             </ul>
                        </div>

                        {/* KROPKA */}
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-purple-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-purple-500">2</span>
                        </div>

                        {/* TEKST (Prawy) */}
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-2 lg:order-3 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-purple-500/10 text-purple-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 02</span>
                            <h3 className="text-xl font-bold text-white mb-2">Design & UX</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Projektujemy wizualną stronę projektu. Skupiamy się na tym, aby strona była nie tylko piękna, ale przede wszystkim intuicyjna i sprzedawała.
                            </p>
                        </div>
                    </div>

                    {/* ETAP 3 (Lewo na Desktop) */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group lg:mb-12">
                        {/* TEKST */}
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-2 lg:order-1 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-orange-500/10 text-orange-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 03</span>
                            <h3 className="text-xl font-bold text-white mb-2">Development (Kodowanie)</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Nasi programiści przekuwają projekt w działający kod. Wdrażamy CMS, optymalizujemy obrazy i dbamy o czystość kodu pod Google.
                            </p>
                        </div>
                        {/* KROPKA */}
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-orange-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-orange-500">3</span>
                        </div>
                        {/* DETALE */}
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-3 mt-4 lg:mt-0">
                             <ul className="text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Next.js & React</li>
                                <li className="flex items-center gap-2">🔹 Integracja CMS</li>
                                <li className="flex items-center gap-2">🔹 Testy wydajności</li>
                             </ul>
                        </div>
                    </div>

                    {/* ETAP 4 (Prawo na Desktop) */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between group">
                        
                        {/* DETALE (Lista) */}
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-0 lg:pr-12 lg:text-right order-3 lg:order-1 mt-4 lg:mt-0">
                             {/* MOBILE: Block A (Left Aligned) */}
                             <ul className="lg:hidden text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5 inline-block w-full">
                                <li className="flex items-center gap-2">🔹 Konfiguracja domeny</li>
                                <li className="flex items-center gap-2">🔹 Google Analytics</li>
                                <li className="flex items-center gap-2">🔹 Szkolenie z obsługi</li>
                             </ul>

                             {/* DESKTOP: Block B (Right Aligned / Reversed) */}
                             <ul className="hidden lg:inline-block w-full text-xs text-gray-400 space-y-1 bg-white/5 p-4 rounded-xl border border-white/5">
                                <li className="flex items-center gap-2 justify-end">Konfiguracja domeny 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Google Analytics 🔹</li>
                                <li className="flex items-center gap-2 justify-end">Szkolenie z obsługi 🔹</li>
                             </ul>
                        </div>

                        {/* KROPKA */}
                        <div className="absolute left-4 lg:left-1/2 w-8 h-8 lg:w-10 lg:h-10 -translate-x-1/2 bg-[#0b0c0e] border-4 border-green-500 rounded-full flex items-center justify-center z-10 shadow-sm order-1">
                            <span className="text-[10px] lg:text-xs font-bold text-green-500">4</span>
                        </div>

                        {/* TEKST (Prawy) */}
                        <div className="w-full pl-12 lg:w-[45%] lg:pl-12 order-2 lg:order-3 mt-2 lg:mt-0">
                            <span className="inline-block px-2 py-1 bg-green-500/10 text-green-400 text-[10px] font-bold uppercase tracking-wider rounded mb-2">Etap 04</span>
                            <h3 className="text-xl font-bold text-white mb-2">Launch & Support</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Wielki start! Podpinamy domenę, zgłaszamy stronę do Google i przekazujemy Ci klucze. Zapewniamy też wsparcie powdrożeniowe.
                            </p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
      </section>


      {/* --- FAQ (ACCORDION) --- */}
      <section className="py-24 px-6 bg-white border-t border-gray-100">
        <div className="mx-auto max-w-3xl">
            {/* NAGŁÓWEK DO LEWEJ NA MOBILE */}
            <div className="text-left lg:text-center mb-16">
                <span className="text-blue-600 font-bold tracking-widest uppercase text-xs mb-3 block">Wiedza</span>
                <h2 className="text-3xl font-bold text-gray-900">Najczęściej zadawane pytania</h2>
            </div>
            
            <div className="space-y-4">
                {faqData.map((item, index) => (
                    <FaqItem 
                        key={index} 
                        question={item.q} 
                        answer={item.a} 
                        isOpen={openFaqIndex === index}
                        onClick={() => toggleFaq(index)}
                    />
                ))}
            </div>
        </div>
      </section>

      {/* --- CTA (NOWA SEKCJA) --- */}
      <section className="py-32 px-6 relative overflow-hidden">
        <GridPattern />
        <div className="mx-auto max-w-4xl text-center relative z-10">
            <div className="inline-block p-4 rounded-full bg-blue-100 text-blue-600 mb-6">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">Gotowy na zmianę?</h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
                Przestań tracić klientów przez wolną i nieczytelną stronę. Zbudujmy serwis, który stanie się Twoim najlepszym handlowcem.
            </p>
            <Link href="/kontakt" className="inline-block rounded-full bg-black px-10 py-5 text-lg font-bold text-white shadow-xl hover:scale-105 hover:bg-gray-800 transition-all">
                Zamów darmową wycenę
            </Link>
        </div>
      </section>

    </main>
  );
}